import { use, useEffect, useRef, useState, type ReactNode } from 'react';
import { type AnyExtension, type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
  AArrowUp,
  AArrowDown,
  Undo2,
  Redo2,
  ImagePlus,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  FONT_SIZES,
  htmlToMarkdown,
  markdownToHtml,
  sanitizeRichText,
} from '../../lib/rich-text-markdown';
import { markApmFileImages, resolveApmFiles } from '../../lib/apm-file';
import { errorMessage } from '../../lib/error-message';
import { ApiRequestError } from '../../lib/http';
import { cn } from '../../lib/utils';
import { EmptyDocFlag } from './empty-doc-flag';
import type * as ImageKit from './rich-text-image';

/**
 * Bật ảnh trong editor — chỉ tài liệu `.md` cá nhân dùng (APM-MYF-f,
 * docs/plan-my-files.md §2.5). Không truyền thì editor y như trước: không nút,
 * không dán/kéo ảnh, không nạp extension ảnh.
 */
export interface RichTextImages {
  /** Tải ảnh lên "Tệp của tôi"; `url` là presigned GET để hiện ngay. */
  upload(file: File): Promise<{ fileId: string; url: string }>;
  /** URL đã ký cho các `apm-file:{id}` có sẵn trong `value` (`null` = ảnh không còn). */
  readonly urls?: Readonly<Record<string, string | null>>;
  /**
   * Lý do **server** nói vì sao chưa tải lên được (`GET /files/capabilities`).
   * Có ⇒ nút chèn ảnh khoá kèm lý do, và dán/kéo ảnh hiện lý do thay vì im lặng.
   */
  readonly disabledReason?: string | null;
}

type ImageModule = typeof ImageKit;
let imageModule: Promise<ImageModule> | undefined;
/** Một promise dùng chung: `use()` cần cùng một đối tượng giữa các lần render. */
function loadImageModule(): Promise<ImageModule> {
  imageModule ??= import('./rich-text-image');
  return imageModule;
}

function altOf(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}

/**
 * Editor cho nơi tự dựng thanh công cụ (`toolbar="none"`) — hộp soạn tài liệu
 * dùng thanh bên kiểu Photoshop (APM-MYF-g).
 */
export interface RichTextHandle {
  readonly editor: Editor;
  /** Mở hộp chọn ảnh; `null` khi editor không bật `images`. */
  readonly pickImages: (() => void) | null;
  /** Mở hộp "Chọn tệp" để chèn tệp của tôi — chỉ `.chilldoc` (APM-CD-c). */
  readonly pickFiles?: () => void;
  /** Font Google của tài liệu — chỉ trình soạn `.chilldoc` có (APM-CD-b). */
  readonly fonts?: { readonly list: readonly string[]; readonly add: (name: string) => void };
  /** Mở hộp căn chỉnh định dạng — chỉ `.chilldoc` (APM-CD-h). */
  readonly formatDocument?: (mode: 'manual' | 'ai') => void;
  /** Nút "Căn chỉnh bằng AI" có hiện không — ẩn khi người dùng thiếu quyền (APM-CD-h). */
  readonly aiFormat?: boolean;
  /** Mở hộp soạn công thức để chèn — chỉ `.chilldoc` (APM-CD-j). */
  readonly insertEquation?: () => void;
  /** Mở trình vẽ cho một khu vực vẽ mới cỡ W×H — chỉ `.chilldoc` (APM-DRW-c). */
  readonly insertDrawing?: (box: { readonly width: number; readonly height: number }) => void;
  /** Mở hộp "Vẽ sơ đồ bằng AI" cho đoạn đã bôi; không có ⇒ nút ẩn (thiếu quyền) — APM-DRW-h. */
  readonly diagramWithAi?: (() => void) | null;
  /** Mở hộp "Viết chi tiết bằng AI" cho mục ở con trỏ; không có ⇒ nút ẩn — APM-SWR-e. */
  readonly writeSection?: (() => void) | null;
  /** Mở hộp Dán thông minh — chỉ `.chilldoc` (APM-SP-c). */
  readonly smartPaste?: () => void;
  /** Mở hộp "Nhập từ tệp" (PDF / .docx) — chỉ `.chilldoc` (APM-CDIMP-e). */
  readonly importDocument?: () => void;
  /** Bình luận — chỉ `.chilldoc` (APM-CMT-b): soạn cho chỗ đang chọn, mở một luồng. */
  readonly comments?: { readonly add: () => void; readonly open: (id: string) => void };
  /** Mở ô Tìm / Thay thế — chỉ `.chilldoc` (APM-CD-y). */
  readonly findReplace?: (mode: 'find' | 'replace') => void;
  /** Hộp chọn đích tham chiếu chéo / hộp đặt bookmark — chỉ `.chilldoc` (APM-CD2-d). */
  readonly references?: { readonly pick: () => void; readonly bookmark: () => void };
  /** Chế độ đề xuất — chỉ `.chilldoc` (APM-CD2-h): ngăn Xem lại, bị ép bật hay không. */
  readonly review?: {
    readonly open: boolean;
    readonly toggle: () => void;
    readonly forced: boolean;
  };
}

/** `none`: không khung, không thanh nút, không dòng gợi ý — chỗ gọi tự lo. */
const FRAME_CLASS = {
  default:
    'rounded-field border border-input bg-background focus-within:ring-2 focus-within:ring-ring',
  none: 'bg-background',
} as const;

const IMAGE_STATUS_CLASS = {
  default: 'border-t border-input',
  none: 'sticky bottom-0 border-t border-input bg-background',
} as const;

export function richTextExtensions({ codeBlock = true }: { readonly codeBlock?: boolean } = {}) {
  return [
    StarterKit.configure({
      link: { openOnClick: false },
      trailingNode: false,
      // Chỗ gọi đưa khối code riêng (hộp soạn `.md`: sơ đồ Mermaid, APM-MMD-a).
      ...(codeBlock ? {} : { codeBlock: false as const }),
    }),
    TextStyle,
    FontSize,
    // Dòng gợi ý hiện theo tài liệu, không theo hình dạng DOM — xem `EmptyDocFlag`.
    EmptyDocFlag,
  ];
}

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  toolbarExtra,
  children,
  images,
  contentClassName,
  toolbar = 'default',
  onEditor,
  extensions,
  listHelp = true,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly placeholder?: string;
  readonly toolbarExtra?: ReactNode;
  readonly children?: ReactNode;
  /** Mặc định tắt — xem `RichTextImages`. Bật/tắt phải cố định suốt đời editor. */
  readonly images?: RichTextImages;
  /** Thay chiều cao mặc định của vùng soạn (hộp soạn toàn màn hình). */
  readonly contentClassName?: string;
  /** Mặc định giữ thanh nút cũ — to-do, task, dự án không đổi gì. */
  readonly toolbar?: 'default' | 'none';
  /** Nhận editor khi đã dựng xong (và `null` khi gỡ) để dựng thanh nút riêng. */
  readonly onEditor?: (handle: RichTextHandle | null) => void;
  /**
   * Extension thêm — cố định suốt đời editor. Có node `codeBlock` riêng thì bỏ
   * khối code của StarterKit.
   */
  readonly extensions?: readonly AnyExtension[];
  /** Dòng gợi ý Tab / Shift+Tab dưới vùng soạn — ô bình luận tắt cho gọn (APM-CMT-b). */
  readonly listHelp?: boolean;
}) {
  const { t } = useTranslation();
  const lastValue = useRef(value);
  // Chunk ảnh chỉ nạp khi bật `images`; `use` treo component tới khi nạp xong.
  const imageKit = images ? use(loadImageModule()) : null;
  const imagesRef = useRef(images);
  imagesRef.current = images;
  // URL của ảnh vừa tải lên trong phiên này — `value` chỉ mang `apm-file:{id}`.
  const uploadedUrls = useRef<Record<string, string>>({});
  const pickerRef = useRef<HTMLInputElement>(null);
  const [uploadingImages, setUploadingImages] = useState(0);
  const [imageError, setImageError] = useState<string | null>(null);

  const toHtml = (markdown: string): string => {
    const current = imagesRef.current;
    if (!current) return markdownToHtml(markdown);
    const urls = { ...current.urls, ...uploadedUrls.current };
    // Thay `apm-file:` → URL **trước** DOMPurify (nó gỡ giao thức lạ), rồi gắn lại id.
    return markApmFileImages(
      markdownToHtml(resolveApmFiles(markdown, urls, { markIds: true }), { images: true }),
    );
  };

  const insertImages = async (target: Editor, files: File[], pos?: number): Promise<void> => {
    const current = imagesRef.current;
    if (!current || !imageKit) return;
    if (current.disabledReason) {
      setImageError(current.disabledReason);
      return;
    }
    setImageError(null);
    let at = pos;
    for (const file of files) {
      if (!imageKit.EDITOR_IMAGE_TYPES.includes(file.type)) {
        setImageError(t('richText.imageType', { name: file.name }));
        continue;
      }
      setUploadingImages((count) => count + 1);
      try {
        const { fileId, url } = await current.upload(file);
        uploadedUrls.current[fileId] = url;
        if (target.isDestroyed) return;
        const where = Math.min(at ?? target.state.selection.to, target.state.doc.content.size);
        target
          .chain()
          .focus()
          .insertContentAt(where, {
            type: 'image',
            attrs: { src: url, alt: altOf(file.name), fileId },
          })
          .run();
        if (at !== undefined) at = where + 1;
      } catch (error) {
        setImageError(
          error instanceof ApiRequestError || !(error instanceof Error)
            ? errorMessage(error, t)
            : error.message,
        );
      } finally {
        setUploadingImages((count) => count - 1);
      }
    }
  };
  const insertImagesRef = useRef(insertImages);
  insertImagesRef.current = insertImages;

  const editor = useEditor({
    extensions: [
      ...richTextExtensions({
        codeBlock: !extensions?.some((extension) => extension.name === 'codeBlock'),
      }),
      ...(extensions ?? []),
      ...(imageKit
        ? imageKit.imageExtensions(
            (target, files, pos) => void insertImagesRef.current(target, files, pos),
          )
        : []),
    ],
    content: toHtml(value),
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': label,
        'aria-multiline': 'true',
        'data-placeholder': placeholder ?? '',
        class: cn(
          'rich-text-content min-h-32 max-h-72 overflow-y-auto px-3 py-2 text-sm outline-none',
          contentClassName,
        ),
      },
      transformPastedHTML: sanitizeRichText,
    },
    onUpdate: ({ editor: current }) => {
      const markdown = current.isEmpty
        ? ''
        : htmlToMarkdown(current.getHTML(), { images: Boolean(imagesRef.current) });
      lastValue.current = markdown;
      onChange(markdown);
    },
  });

  const onEditorRef = useRef(onEditor);
  onEditorRef.current = onEditor;
  useEffect(() => {
    if (!editor || !onEditorRef.current) return;
    onEditorRef.current({
      editor,
      pickImages: imageKit ? () => pickerRef.current?.click() : null,
    });
    return () => onEditorRef.current?.(null);
  }, [editor, imageKit]);

  useEffect(() => {
    if (editor && value !== lastValue.current) {
      lastValue.current = value;
      editor.commands.setContent(toHtml(value), { emitUpdate: false });
    }
  }, [editor, value]);

  const size = Number.parseInt(String(editor?.getAttributes('textStyle')['fontSize'] ?? '14'), 10);
  const smaller = [...FONT_SIZES].reverse().find((candidate) => candidate < size);
  const larger = FONT_SIZES.find((candidate) => candidate > size);
  function button(
    name: string,
    icon: ReactNode,
    action: () => void,
    active?: boolean,
    disabled = false,
  ) {
    return (
      <button
        type="button"
        title={name}
        aria-label={name}
        aria-pressed={active}
        disabled={!editor || disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={action}
        className={cn(
          'rounded-sm p-1.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-40 disabled:cursor-not-allowed',
          active && 'bg-muted text-primary',
        )}
      >
        {icon}
      </button>
    );
  }

  return (
    <div className={FRAME_CLASS[toolbar]}>
      {toolbar === 'default' ? (
        <div
          role="group"
          aria-label={t('richText.formatting')}
          className="flex flex-wrap items-center gap-0.5 border-b border-input p-1"
        >
          {button(
            t('richText.bold'),
            <Bold className="size-4" />,
            () => editor?.chain().focus().toggleBold().run(),
            editor?.isActive('bold'),
          )}
          {button(
            t('richText.italic'),
            <Italic className="size-4" />,
            () => editor?.chain().focus().toggleItalic().run(),
            editor?.isActive('italic'),
          )}
          {button(
            t('richText.strike'),
            <Strikethrough className="size-4" />,
            () => editor?.chain().focus().toggleStrike().run(),
            editor?.isActive('strike'),
          )}
          {button(
            t('richText.underline'),
            <Underline className="size-4" />,
            () => editor?.chain().focus().toggleUnderline().run(),
            editor?.isActive('underline'),
          )}
          <span aria-hidden className="mx-1 h-5 border-l border-input" />
          {button(
            t('richText.smaller'),
            <AArrowDown className="size-4" />,
            () => smaller && editor?.chain().focus().setFontSize(`${smaller}px`).run(),
            undefined,
            !smaller,
          )}
          <span className="min-w-6 text-center text-xs" aria-label={t('richText.fontSize')}>
            {size}
          </span>
          {button(
            t('richText.larger'),
            <AArrowUp className="size-4" />,
            () => larger && editor?.chain().focus().setFontSize(`${larger}px`).run(),
            undefined,
            !larger,
          )}
          <span aria-hidden className="mx-1 h-5 border-l border-input" />
          {button(
            t('richText.bulletList'),
            <List className="size-4" />,
            () => editor?.chain().focus().toggleBulletList().run(),
            editor?.isActive('bulletList'),
          )}
          {button(
            t('richText.orderedList'),
            <ListOrdered className="size-4" />,
            () => editor?.chain().focus().toggleOrderedList().run(),
            editor?.isActive('orderedList'),
          )}
          {button(
            t('richText.indent'),
            <IndentIncrease className="size-4" />,
            () => editor?.chain().focus().sinkListItem('listItem').run(),
            undefined,
            !editor?.can().sinkListItem('listItem'),
          )}
          {button(
            t('richText.outdent'),
            <IndentDecrease className="size-4" />,
            () => editor?.chain().focus().liftListItem('listItem').run(),
            undefined,
            !editor?.can().liftListItem('listItem'),
          )}
          <span aria-hidden className="mx-1 h-5 border-l border-input" />
          {button(
            t('richText.undo'),
            <Undo2 className="size-4" />,
            () => editor?.chain().focus().undo().run(),
            undefined,
            !editor?.can().undo(),
          )}
          {button(
            t('richText.redo'),
            <Redo2 className="size-4" />,
            () => editor?.chain().focus().redo().run(),
            undefined,
            !editor?.can().redo(),
          )}
          {images && imageKit ? (
            <>
              <span aria-hidden className="mx-1 h-5 border-l border-input" />
              <button
                type="button"
                aria-label={t('richText.insertImage')}
                // Khoá mà không nói vì sao là thứ khiến người dùng đi mở ticket.
                title={images.disabledReason ?? t('richText.insertImage')}
                disabled={!editor || Boolean(images.disabledReason)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickerRef.current?.click()}
                className="rounded-sm p-1.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ImagePlus className="size-4" />
              </button>
            </>
          ) : null}
          {toolbarExtra}
        </div>
      ) : null}
      {images && imageKit ? (
        <input
          ref={pickerRef}
          type="file"
          hidden
          multiple
          accept={imageKit.EDITOR_IMAGE_TYPES.join(',')}
          data-testid="rich-text-image-input"
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            if (editor && files.length > 0) void insertImages(editor, files);
          }}
        />
      ) : null}
      <EditorContent editor={editor} />
      {images && (uploadingImages > 0 || imageError || images.disabledReason) ? (
        <p
          role={imageError ? 'alert' : 'status'}
          className={cn(
            IMAGE_STATUS_CLASS[toolbar],
            'px-3 py-1.5 text-xs',
            imageError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {uploadingImages > 0
            ? t('richText.uploadingImages', { count: uploadingImages })
            : (imageError ?? t('richText.imagesDisabled', { reason: images.disabledReason }))}
        </p>
      ) : null}
      {children}
      {toolbar === 'default' && listHelp ? (
        <p className="border-t border-input px-3 py-1.5 text-xs text-muted-foreground">
          {t('richText.listHelp')}
        </p>
      ) : null}
    </div>
  );
}
